<template>
    <form-group
        ref="selectGroupRef"
        :group-classes="formGroupClasses"
        :horizontal="horizontal"
        :inline="inline"
        :label="label"
        :label-classes="[{ required }]"
    >
        <slot v-if="readonly" name="readonly">
            <span v-html="selectedValue" />
        </slot>
        <Popover v-else v-model:open="isOpen">
            <PopoverAnchor>
                <PopoverTrigger
                    :class="[
                        {
                            '!border-primary': isOpen,
                            'border-danger': !!invalidFeedback,
                            '!bg-secondary': !inline && isDisabled,
                            'opacity-50': isDisabled,
                            'w-100 !rounded-xl border bg-white px-2 py-3': !inline,
                        },
                        addTogglerClasses,
                    ]"
                    :data-testid="`select-${name}`"
                    :disabled="isDisabled"
                    class="focus-visible:outline-none"
                >
                    <slot :disabled="disabled" :placeholder="placeholder" :props="selectedItem" name="toggler-content">
                        <div class="flex min-w-0 items-center justify-between gap-1">
                            <slot :placeholder="placeholder" :selectedItem="selectedItem" name="selectedValue">
                                <div
                                    :class="['min-w-0 flex-grow text-start', wrapOptions ? 'break-words whitespace-normal' : 'truncate']"
                                    v-html="selectedValue"
                                />
                            </slot>
                            <spinner v-if="isLoading" color="white" style="position: absolute; right: 4px" />
                            <ChevronsUpDown
                                v-else
                                :class="{ 'text-danger': !!invalidFeedback && !isOpen }"
                                class="ml-2 h-4 w-4 shrink-0 opacity-75"
                            />
                        </div>
                    </slot>
                </PopoverTrigger>
            </PopoverAnchor>
            <PopoverContent
                :align="align"
                :align-flip="true"
                :class="['popover-content p-0', wrapOptions ? 'popover-content--wrap' : '']"
                :disable-popover-portal="true"
                :hide-when-detached="true"
                :prioritize-position="true"
            >
                <Command :highlight-on-hover="false" :model-value="modelValue" selection-behavior="manual">
                    <CommandInput v-if="searchEnabled" :placeholder="trans('general.placeholder.search')" />
                    <CommandList>
                        <CommandEmpty class="p-2 text-start text-base">{{ trans('general.selection.options.empty') }}</CommandEmpty>
                        <CommandGroup v-if="!required && resettable && options.length">
                            <CommandItem
                                :value="defaultValue"
                                class="rounded-lg p-2 !text-base hover:bg-primary hover:text-white focus:bg-primary focus:text-white"
                                @select="
                                    () => {
                                        selectValue(defaultValue);
                                    }
                                "
                            >
                                {{ resetLabel }}
                            </CommandItem>
                        </CommandGroup>
                        <CommandGroup
                            v-for="group in groupedOptions"
                            :heading="group.group === 'default' ? null : group.group"
                            class="flex flex-col gap-[1px]"
                        >
                            <CommandItem
                                v-for="option in group.data"
                                :key="option.value"
                                :class="[
                                    inline ? 'px-2 py-1' : 'p-2',
                                    option.value === modelValue ? 'bg-primary text-white' : '',
                                    wrapOptions ? 'whitespace-normal' : 'truncate',
                                ]"
                                :data-testid="`option-${option.value}`"
                                :value="option.value"
                                as="div"
                                class="group block rounded-lg !text-base hover:bg-primary hover:text-white"
                                @select="
                                    () => {
                                        selectValue(option.value);
                                    }
                                "
                            >
                                <slot :option="option" name="item-label">
                                    <span v-html="option.label" />
                                </slot>
                            </CommandItem>
                        </CommandGroup>
                    </CommandList>
                </Command>
                <div v-if="$slots.actions" class="border-top p-0" @click="isOpen = false">
                    <dropdown-divider class="m-0" />
                    <slot name="actions" />
                </div>
            </PopoverContent>
        </Popover>
        <slot name="description">
            <div v-if="description">
                <small class="form-text w-100 text-muted" v-text="description" />
            </div>
        </slot>
        <div v-if="invalidFeedback" class="mt-1 text-sm text-[13px] text-danger" v-text="invalidFeedback" />
    </form-group>
</template>
<script setup>
import FormGroup from '@/Components/FormGroup.vue';
import { ChevronsUpDown } from '@lucide/vue';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/Components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/Components/ui/popover';
import { computed, onUnmounted, ref, watch } from 'vue';
import { trans } from '@/mixins/i18n.js';
import Spinner from '@/Components/Spinner.vue';
import DropdownDivider from '@/Components/DropdownDivider.vue';

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
    id: String,
    name: String,
    label: String,
    required: Boolean,
    inline: Boolean,
    modelValue: String,
    options: Array,
    disabled: Boolean,
    isLoading: Boolean,
    formGroupClasses: [String, Object, Array],
    readonly: Boolean,
    horizontal: {
        default: true,
    },
    invalidFeedback: {
        type: String,
        default: '',
    },
    placeholder: {
        type: String,
        default: () => trans('general.form.select.placeholder'),
    },
    description: {
        type: String,
        default: '',
    },
    enableSearch: {
        type: Boolean,
        default: true,
    },
    defaultValue: {
        type: String,
        default: null,
    },
    addTogglerClasses: {
        type: String,
        default: '',
    },
    resettable: {
        type: Boolean,
        default: true,
    },
    resetLabel: {
        type: String,
        default: () => trans('general.dropdown.reset.label'),
    },
    searchPlaceholder: {
        type: String,
        default: () => trans('general.placeholder.search'),
    },
    align: {
        type: String,
        default: 'auto',
    },
    wrapOptions: {
        type: Boolean,
        default: false,
    },
});

const isOpen = ref(false);
const selectGroupRef = ref(null);

const groupedOptions = computed(() => {
    let groups = [];

    (props.options ?? []).forEach((option) => {
        const groupName = option.group ?? 'default';
        let data = option.data ?? [option];
        let groupContainer = groups.find((g) => g.group === groupName);

        if (groupContainer) {
            groupContainer.data.push(...data);
        } else {
            groups.push({ group: groupName, data: data });
        }
    });

    return groups;
});

const optionsCount = computed(() => groupedOptions.value?.reduce((count, group) => count + (group.data?.length ?? 0), 0) ?? 0);
const selectedItem = computed(() => {
    let item = null;

    groupedOptions.value.find((group) => {
        item = group.data.find((el) => el.value === props.modelValue);
        return item ?? false;
    });

    return item;
});
const selectedValue = computed(() => selectedItem.value?.label ?? props.placeholder);
const searchEnabled = computed(() => props.enableSearch && optionsCount.value > 5);
const isDisabled = computed(() => props.disabled);

watch(
    () => props.options,
    () => {
        // If there is exactly one selectable option, and the field is required, auto-select it.
        // Use the normalized leaf options (groupedOptions), not the raw `options`: for grouped
        // options each top-level entry is a { group, data } container with no `value`, so reading
        // `options[0].value` would emit `undefined` and silently clear the model whenever there is
        // a single group (e.g. an in-progress work order's assignee list).
        if (!props.required) return;

        const leafOptions = groupedOptions.value.flatMap((group) => group.data ?? []);
        if (leafOptions.length !== 1) return;

        const onlyValue = leafOptions[0]?.value;
        if (onlyValue === undefined || onlyValue === props.modelValue) return;

        emit('update:modelValue', onlyValue);
    },
    { deep: true, immediate: true },
);

const selectValue = (value) => {
    emit('update:modelValue', value);
    isOpen.value = false;
};

const closeOnScroll = (event) => {
    if (!selectGroupRef.value?.$el || selectGroupRef.value.$el.contains(event.target)) return;
    isOpen.value = false;
};

watch(isOpen, (open) => {
    if (open) {
        window.addEventListener('scroll', closeOnScroll, { capture: true, passive: true });
    } else {
        window.removeEventListener('scroll', closeOnScroll, { capture: true });
    }
});

onUnmounted(() => {
    window.removeEventListener('scroll', closeOnScroll, { capture: true });
});
</script>
<style>
.popover-content {
    width: max-content;
    min-width: var(--reka-popover-trigger-width);
    max-width: var(--reka-popover-content-available-width, 100vw);
    max-height: var(--reka-popover-content-available-height);
}
.popover-content.popover-content--wrap {
    width: var(--reka-popover-trigger-width);
}
</style>
